import { JUMP_FLOURISH } from "./animation_pose"
import { runningJumpLift } from "./jump"
import { collisionMover, CollisionMover, Coordinate, Entity, getCurrentTime } from "./models"
import { roofCellAt, wallCellAt } from "./building_levels"
import { isBlockWallItem } from "./room/block_walls"
import { TileMapMetaProvider } from "./tile_map_meta_provider"

// Clear the raised wall face with the feet, rather than turning walls off at the key press.
const WALL_CLEARANCE_PX = 20
const clearsWall = (startedAt: number, now: number): boolean => runningJumpLift(now - startedAt) >= WALL_CLEARANCE_PX

// A move heads along an axis when it covers at least tan 22.5° as much ground on it as on the other: the nearest of eight.
const AXIS_SHARE = Math.tan(Math.PI / 8)
const headingSign = (along: number, across: number): number => Math.abs(along) >= AXIS_SHARE * Math.abs(across) ? Math.sign(along) : 0

/** The cells next to `cell` on the side a move heading (sx, sy) goes towards: straight across, and diagonally too for a diagonal. */
const sideCells = (cell: Coordinate, sx: number, sy: number): Coordinate[] => {
    const cells: Coordinate[] = []
    if (sx) cells.push({ x: cell.x + sx, y: cell.y })
    if (sy) cells.push({ x: cell.x, y: cell.y + sy })
    if (sx && sy) cells.push({ x: cell.x + sx, y: cell.y + sy })
    return cells
}

/** After a quarter-second run-up a jump clears thin walls. Solid blocks use the wall-mount landing path. */
export const playerCollisionMover = (map: TileMapMetaProvider, entity: Entity, now = getCurrentTime()): CollisionMover => {
    const jump = entity.jump
    const predicted = entity.predictedFlourish
    const running = entity.isPlayer() && (
        jump && !jump.mount && !jump.landed && jump.running && jump.clearsWalls && clearsWall(jump.startedAt, now) ||
        predicted?.view.action === JUMP_FLOURISH && !predicted.view.mount && predicted.runningJump && predicted.clearsWalls &&
            clearsWall(predicted.at, now))
    if (!running) return collisionMover(map)

    // Wall caps belong to the wall on the row below. Filter both its footprint and its
    // legacy collision cell; trees, furniture and walls with another storey above still block.
    const clear = (x: number, y: number, sx: number, sy: number): boolean => {
        const parent = map.getMapDeltaAt(x, y, "sprite")?.context?.parent
        const cell = wallCellAt(map, { x, y }) ? { x, y } : parent
        if (!cell || !wallCellAt(map, cell) || isBlockWallItem(map.wallItemAt(cell.x, cell.y))) return false
        const upper = map.getUpperFloorProvider()
        if (upper && (wallCellAt(upper, cell) || upper.isCollisionAt(cell.x, cell.y) || upper.isStairAt(cell.x, cell.y))) return false
        // A roof or a ceiling on the wall, or over the ground on either side of it along the move, closes it: no
        // vaulting into or out of a roofed house, or through a roof drawn over the wall. Mounting a solid block still
        // uses wallTopOpenAt and its roof restriction.
        return ![cell, ...sideCells(cell, sx, sy), ...sideCells(cell, -sx, -sy)].some(at =>
            roofCellAt(map, at) || map.isRoofCoveredAt(at.x, at.y))
    }
    // Which cells lie either side of a wall depends on the move, which both client and server know.
    return (bounds, translate, result, adjust) => {
        const sx = headingSign(translate.x, translate.y), sy = headingSign(translate.y, translate.x)
        const airborne = Object.create(map) as TileMapMetaProvider
        airborne.isCollisionAt = (x, y, ...args) => !clear(x, y, sx, sy) && map.isCollisionAt(x, y, ...args)
        airborne.getTileBlockingProfile = (x, y) => clear(x, y, sx, sy) ? [] : map.getTileBlockingProfile(x, y)
        collisionMover(airborne)(bounds, translate, result, adjust)
    }
}
